import {
  AutoCompleteUncontrolled,
  ISelectType,
  Table,
} from "@4uhub/lib4uhub";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import FiltersActions from "../../../../components/FiltersActions/FiltersActions";
import useFilter from "../../../../hooks/useFilter";
import {
  ICalendarControlItemXl,
  ICalendarControlXl,
} from "../../../../models/calendar-control";
import MainApi from "../../../../services/mainApi.service";
import {
  CALENDAR_CONTROL_ITEM_ROUTE,
  ITEM_STATUS_VALUES,
  formatDateTime,
  formatDocument,
  itemStatusConfig,
  translationPath,
} from "./utils";

const ITEM_ROW_HEIGHT = 56;
const ITEM_MESSAGE_MAX_LINES = 2;

const calendarControlItemService = new MainApi<ICalendarControlItemXl>(
  CALENDAR_CONTROL_ITEM_ROUTE,
);

interface IItemFilterParams {
  CalendarIdentifier: string;
  Status: string;
  IsValid: string; // "" | "true" | "false"
}

interface ICalendarControlItemsModalProps {
  calendarControl: ICalendarControlXl;
  onClose: () => void;
}

const initialFilter: IItemFilterParams = {
  CalendarIdentifier: "",
  Status: "",
  IsValid: "",
};

const CalendarControlItemsModal: React.FC<ICalendarControlItemsModalProps> = ({
  calendarControl,
  onClose,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [refresh, setRefresh] = useState(0);
  const [filterParams, setFilterParams] = useState<IItemFilterParams>(initialFilter);
  const { filter, showCleanBtn, initialState, setFilter, cleanFilter } =
    useFilter(filterParams);

  const label = useCallback(
    (key: string, fallback: string) => t(translationPath + key) || fallback,
    [t],
  );

  const statusOptions = useMemo<ISelectType[]>(
    () =>
      ITEM_STATUS_VALUES.map((value) => ({
        id: value,
        name: t(translationPath + `item_status_values.${value}`) || value,
      })),
    [t],
  );

  const isValidOptions = useMemo<ISelectType[]>(
    () => [
      { id: "true", name: label("is_valid_values.S", "Válido") },
      { id: "false", name: label("is_valid_values.N", "Inválido") },
    ],
    [label],
  );

  const columns: GridColDef<ICalendarControlItemXl>[] = useMemo(
    () => [
      {
        field: "calendarIdentifier",
        headerName: label("items.calendar_identifier", "Agenda"),
        width: 110,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => params.value ?? "-",
      },
      {
        field: "slotIdentifier",
        headerName: label("items.slot_identifier", "Identificador do slot"),
        width: 180,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => params.value ?? "-",
      },
      {
        field: "calendarDate",
        headerName: label("items.calendar_date", "Data e horário"),
        width: 170,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => formatDateTime(params.value, language),
      },
      {
        field: "status",
        headerName: label("items.status", "Status"),
        width: 130,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          if (!params.value) return "-";
          const cfg = itemStatusConfig[params.value];
          return (
            <Chip
              label={cfg ? t(translationPath + `item_status_values.${cfg.key}`) : params.value}
              color={cfg?.color ?? "default"}
              size="small"
            />
          );
        },
      },
      {
        field: "isValid",
        headerName: label("items.is_valid", "Válido"),
        width: 110,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          if (!params.value) return "-";
          const valid = params.value === "S";
          return (
            <Chip
              label={label(`is_valid_values.${valid ? "S" : "N"}`, valid ? "Sim" : "Não")}
              color={valid ? "success" : "error"}
              size="small"
            />
          );
        },
      },
      {
        field: "message",
        headerName: label("items.message", "Motivos"),
        flex: 1,
        minWidth: 320,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => (
          <Tooltip title={params.value || ""} placement="top-start" enterDelay={400}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: "0.8rem",
                lineHeight: 1.35,
                // Corta com "…" quando o motivo não cabe nas linhas da célula; o texto completo fica no tooltip.
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: ITEM_MESSAGE_MAX_LINES,
                overflow: "hidden",
              }}
            >
              {params.value || "-"}
            </Typography>
          </Tooltip>
        ),
      },
    ],
    [label, language, t],
  );

  const applyFilter = useCallback(() => {
    setFilterParams(filter);
    setRefresh((prev) => prev + 1);
  }, [filter]);

  const cleanHandler = useCallback(() => {
    setFilterParams(initialState.current);
    cleanFilter();
    setRefresh((prev) => prev + 1);
  }, [cleanFilter, initialState]);

  const serviceProps = useMemo(
    () => ({
      refresh,
      IdControl: calendarControl.identifier,
      CalendarIdentifier: filterParams.CalendarIdentifier || undefined,
      Status: filterParams.Status || undefined,
      IsValid: filterParams.IsValid || undefined,
    }),
    [refresh, calendarControl.identifier, filterParams],
  );

  const macroFilters = useCallback(
    () => (
      <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap" sx={{ width: "100%" }}>
        <Box sx={{ flex: "1 1 160px", minWidth: 150 }}>
          <TextField
            value={filter.CalendarIdentifier}
            size="small"
            label={label("items.calendar_identifier", "Agenda")}
            fullWidth
            onChange={(e) =>
              setFilter({ CalendarIdentifier: e.target.value.replace(/\D/g, "") })
            }
            inputProps={{ inputMode: "numeric" }}
          />
        </Box>
        <Box sx={{ flex: "1 1 180px", minWidth: 160 }}>
          <AutoCompleteUncontrolled
            label={label("items.status", "Status")}
            onValueSelected={(value) => setFilter({ Status: value ? String(value.id) : "" })}
            value={statusOptions.find((o) => o.id === filter.Status) ?? null}
            options={statusOptions}
            size="small"
            fullWidth
          />
        </Box>
        <Box sx={{ flex: "1 1 160px", minWidth: 150 }}>
          <AutoCompleteUncontrolled
            label={label("items.is_valid", "Válido")}
            onValueSelected={(value) => setFilter({ IsValid: value ? String(value.id) : "" })}
            value={isValidOptions.find((o) => o.id === filter.IsValid) ?? null}
            options={isValidOptions}
            size="small"
            fullWidth
          />
        </Box>
        <FiltersActions onFilter={applyFilter} onClean={cleanHandler} showCleanBtn={showCleanBtn} />
      </Stack>
    ),
    [filter, setFilter, statusOptions, isValidOptions, applyFilter, cleanHandler, showCleanBtn, label],
  );

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      // Altura fixa: o modal não cresce com o conteúdo — quem rola é a grid.
      PaperProps={{ sx: { height: "85vh", maxHeight: "85vh" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div">
          {label("items.title", "Detalhe da busca")} - {calendarControl.identifier}
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          {formatDocument(calendarControl.document)} ·{" "}
          {formatDateTime(calendarControl.created, language)} ·{" "}
          {calendarControl.typeOfAppointment
            ? t(translationPath + `type_of_appointment_values.${calendarControl.typeOfAppointment}`) ||
              calendarControl.typeOfAppointment
            : "-"}
        </Typography>
      </DialogTitle>
      <DialogContent
        dividers
        // overflow hidden: a rolagem fica dentro da grid, mantendo filtros e rodapé sempre visíveis.
        sx={{ p: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            // O Table da lib usa height:100% no Paper da grid, o que a faria crescer além
            // do modal. Aqui ela passa a ocupar apenas o espaço restante.
            "& > .MuiStack-root": { height: "100%", minHeight: 0, pt: 0 },
            "& > .MuiStack-root > .MuiPaper-root": {
              flex: 1,
              minHeight: 0,
              height: "auto",
              mt: 1,
            },
            // A lib fixa checkboxSelection e não expõe prop para desligar.
            // !important: as regras do próprio DataGrid têm a mesma especificidade e prevalecem sem ele.
            "& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox": {
              display: "none !important",
            },
          }}
        >
          <Table<ICalendarControlItemXl>
            columns={columns}
            service={calendarControlItemService}
            serviceProps={serviceProps}
            macroFilters={macroFilters}
            getRowId={(row) => row.identifier ?? `${row.idControl}-${row.slotIdentifier}`}
            getRowHeight={() => ITEM_ROW_HEIGHT}
            defaultPageSize={100}
            pageSizeOptions={[25, 50, 100, 200, 500]}
            searchInput={false}
            searchInputPlaceHolder=""
            showDefaultMacroFilters={false}
            showButton={false}
            showDeleteButton={false}
            defaultActions={false}
            hideLog
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{label("close", "Fechar")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CalendarControlItemsModal;
